/**
 * EditorToolbar Component
 * 
 * Main toolbar for the ContinuousDocument editor with all formatting controls.
 *
 * T-121a: los SelectValue de fuente/tamaño ahora llevan children en vez de
 *   placeholder, porque currentFontSize/currentFontFamily nunca son '' (su
 *   default es 'default') y el placeholder nunca se pintaba — el trigger
 *   mostraba el label largo del item ("Tamaño (Normal)") y se desbordaba del
 *   recuadro. Con children controlamos el texto corto del trigger. Se añade
 *   además `[&>span]:truncate` al trigger como guarda.
 * T-121b: bajo 1024px (useIsDesktop === false) el toolbar se compacta a
 *   [Fuente][Tamaño][B][slot rotativo][menu de desborde][Chat]. El slot muestra
 *   la última acción elegida del menú de desborde (default: Itálica); elegir un
 *   item del menú ejecuta la acción Y la lleva al slot. En desktop la barra queda
 *   idéntica a antes (salvo el fix T-121a). El menú de desborde replica el patrón
 *   de foco/selección de los Select de fuente/tamaño (onPointerDownCapture que
 *   captura el rango, open controlado, modal={false}, onCloseAutoFocus que
 *   impide que Radix devuelva el foco al trigger tras onSelect).
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../ui/button';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '../ui/dropdown-menu';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Highlighter,
  ImageIcon,
  Images,
  Sparkles,
  TableIcon,
  MessageSquare,
  MoreHorizontal,
  Pilcrow
} from 'lucide-react';
import { NodeSelection } from 'prosemirror-state';
import { useT } from '../../../i18n';
import useIsDesktop from '../../../hooks/useIsDesktop';
import ToolbarButton from './ToolbarButton';

/**
 * EditorToolbar component
 * 
 * Args:
 *   editor (object): TipTap editor instance
 *   savedSelectionRef (ref): Reference to saved selection state
 *   selectDropdownSelectionRef (ref): Reference to selection before dropdown opens
 *   onImageUpload (function): Handler for image upload button
 *   onImageGenerate (function): Handler for image generation button
 *   onInsertTable (function): Handler for table insertion button
 *   alignTableOrSelection (function): Handler for alignment changes
 * 
 * Returns:
 *   JSX.Element: The editor toolbar
 */
const EditorToolbar = ({
  editor,
  savedSelectionRef,
  selectDropdownSelectionRef,
  onImageUpload,
  onImageGenerate,
  onOpenGallery,
  onInsertTable,
  alignTableOrSelection,
  chatOpen,
  onToggleChat
}) => {
  const t = useT();
  const isDesktop = useIsDesktop();
  const [isFontFamilyOpen, setIsFontFamilyOpen] = useState(false);
  const [isFontSizeOpen, setIsFontSizeOpen] = useState(false);
  // T-121b: slot rotativo del toolbar compacto (NO se persiste — YAGNI).
  const [toolbarSlot, setToolbarSlot] = useState('italic');
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const isDropdownOpenRef = useRef(false);

  useEffect(() => {
    isDropdownOpenRef.current = isFontFamilyOpen || isFontSizeOpen;
  }, [isFontFamilyOpen, isFontSizeOpen]);

  const fontFamilyOptions = useMemo(
    () => [
      { label: 'Arial', value: 'Arial' },
      { label: 'Times New Roman', value: '"Times New Roman"' },
      { label: 'Georgia', value: 'Georgia' },
      { label: 'Courier New', value: '"Courier New"' },
      { label: 'Trebuchet MS', value: '"Trebuchet MS"' }
    ],
    []
  );

  const fontSizeOptions = useMemo(
    () => ['12px', '14px', '16px', '18px', '24px', '32px'],
    []
  );

  const textStyleAttributes = editor.getAttributes('textStyle') || {};
  const currentFontFamily = textStyleAttributes.fontFamily || 'default';
  const currentFontSize = textStyleAttributes.fontSize || 'default';

  // Con una imagen (NodeSelection) seleccionada, editor.isActive({textAlign})
  // e isActive de marcas dan estados espurios (izq y der activos a la vez,
  // resaltado activo sobre una imagen). Para un nodo, la alineación activa se
  // lee del atributo real del nodo y las marcas de texto no se iluminan.
  const selection = editor.state.selection;
  const isNodeSel = selection instanceof NodeSelection;
  const nodeAlign = isNodeSel ? selection.node?.attrs?.textAlign : null;
  const alignActive = (a) => (isNodeSel ? nodeAlign === a : editor.isActive({ textAlign: a }));
  const markActive = (name) => !isNodeSel && editor.isActive(name);

  const runWithSelection = ({ from, to, isNodeSelection }, buildChain) => {
    const nodeSelection = Boolean(isNodeSelection);

    if (nodeSelection) {
      buildChain(editor.chain().focus().setNodeSelection(from)).run();
      return;
    }

    if (from !== to) {
      buildChain(editor.chain().focus().setTextSelection({ from, to }))
        .setTextSelection({ from, to })
        .run();
    } else {
      buildChain(editor.chain().focus()).run();
    }
  };

  if (!editor) {
    return null;
  }

  // T-121b — Acciones del toolbar compacto (slot rotativo + menu de desborde).
  // Reusa EXACTAMENTE el onClick/icon/label/title/active de cada boton de
  // escritorio; no reescribe logica. Bold queda fijo fuera de la lista.
  // `label` genera el data-testid en ToolbarButton (ej. editor-italic): se
  // conservan los labels EXACTOS de cada boton para no romper anclas de test.
  const overflowActions = [
    {
      id: 'italic', icon: Italic, label: 'Italic', title: t('editor.italic'),
      isActive: () => markActive('italic'),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleItalic()),
    },
    {
      id: 'strike', icon: Strikethrough, label: 'Strike', title: t('editor.strike'),
      isActive: () => markActive('strike'),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleStrike()),
    },
    {
      id: 'code', icon: Code, label: 'Code', title: t('editor.code'),
      isActive: () => markActive('code'),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleCode()),
    },
    {
      id: 'paragraph', icon: Pilcrow, label: 'Normal', title: t('editor.normal'),
      isActive: () => editor.isActive('paragraph'),
      // "Normal" (párrafo) no es un ToolbarButton hoy: es un <Button> crudo con
      // su propia logica onMouseDown. Aquí se envuelve TAL CUAL en run (solo se
      // toma from/to del selectionInfo que entrega ToolbarButton/el menu, en
      // vez de editor.state.selection, para que funcione desde el dropdown).
      run: (selectionInfo) => {
        const { from, to } = selectionInfo || editor.state.selection;
        if (from !== to) {
          editor.chain().focus().setTextSelection({ from, to }).setParagraph().setTextSelection({ from, to }).run();
        } else {
          editor.chain().focus().setParagraph().run();
        }
      },
    },
    {
      id: 'heading1', icon: Heading1, label: 'Heading 1', title: t('editor.heading1'),
      isActive: () => editor.isActive('heading', { level: 1 }),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleHeading({ level: 1 })),
    },
    {
      id: 'heading2', icon: Heading2, label: 'Heading 2', title: t('editor.heading2'),
      isActive: () => editor.isActive('heading', { level: 2 }),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleHeading({ level: 2 })),
    },
    {
      id: 'heading3', icon: Heading3, label: 'Heading 3', title: t('editor.heading3'),
      isActive: () => editor.isActive('heading', { level: 3 }),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleHeading({ level: 3 })),
    },
    {
      id: 'bulletList', icon: List, label: 'Bullet List', title: t('editor.bulletList'),
      isActive: () => editor.isActive('bulletList'),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleBulletList()),
    },
    {
      id: 'orderedList', icon: ListOrdered, label: 'Ordered List', title: t('editor.orderedList'),
      isActive: () => editor.isActive('orderedList'),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleOrderedList()),
    },
    {
      id: 'quote', icon: Quote, label: 'Quote', title: t('editor.quote'),
      isActive: () => editor.isActive('blockquote'),
      run: (selectionInfo) => {
        // En una lista, se envuelve/desenvuelve la LISTA COMPLETA en una cita
        // (conservando la lista dentro del blockquote). Fuera de lista, cita
        // normal por bloque. Se toma la lista más externa (shallowest) para que
        // una lista anidada se cite entera.
        const inList = editor.isActive('bulletList') || editor.isActive('orderedList');
        if (inList) {
          const { $from } = editor.state.selection;
          let listPos = null;
          for (let d = 1; d <= $from.depth; d++) {
            const n = $from.node(d);
            if (n.type.name === 'bulletList' || n.type.name === 'orderedList') {
              listPos = $from.before(d);
              break;
            }
          }
          if (listPos != null) {
            editor.chain().focus().setNodeSelection(listPos).toggleBlockquote().run();
            return;
          }
        }
        runWithSelection(selectionInfo, (chain) => chain.toggleBlockquote());
      },
    },
    {
      id: 'alignLeft', icon: AlignLeft, label: 'Align Left', title: t('editor.alignLeft'),
      isActive: () => alignActive('left'),
      run: (selectionInfo) => alignTableOrSelection('left', selectionInfo),
    },
    {
      id: 'alignCenter', icon: AlignCenter, label: 'Align Center', title: t('editor.alignCenter'),
      isActive: () => alignActive('center'),
      run: (selectionInfo) => alignTableOrSelection('center', selectionInfo),
    },
    {
      id: 'alignRight', icon: AlignRight, label: 'Align Right', title: t('editor.alignRight'),
      isActive: () => alignActive('right'),
      run: (selectionInfo) => alignTableOrSelection('right', selectionInfo),
    },
    {
      id: 'highlight', icon: Highlighter, label: 'Highlight', title: t('editor.highlight'),
      isActive: () => markActive('highlight'),
      run: (selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleHighlight()),
    },
    {
      id: 'insertImage', icon: ImageIcon, label: 'Insert Image', title: t('editor.insertImage'),
      isActive: () => false,
      run: () => onImageUpload(),
    },
    {
      id: 'generateImage', icon: Sparkles, label: 'Generate Image', title: t('editor.generateImage'),
      isActive: () => false,
      run: () => onImageGenerate(),
    },
    {
      id: 'gallery', icon: Images, label: 'Gallery', title: 'Galería de imágenes',
      isActive: () => false,
      run: () => onOpenGallery(),
    },
    {
      id: 'insertTable', icon: TableIcon, label: 'Insert Smart Table', title: t('editor.insertTable'),
      isActive: () => false,
      run: () => onInsertTable(),
    },
  ];

  const slotAction = overflowActions.find((a) => a.id === toolbarSlot) || overflowActions[0];

  return (
    <div className="editor-toolbar flex-shrink-0 flex flex-wrap gap-1 p-2 glass rounded-lg border border-white/20 mx-8 mt-4">
      {/* Font Family and Size Selectors (compartidos desktop/compacto; ancho
          condicional por isDesktop). T-121a: SelectValue con children + guarda
          [&>span]:truncate para que el texto corto nunca se desborde. */}
      <div className="flex items-center gap-2 mr-2">
        <Select
          open={isFontFamilyOpen}
          onOpenChange={setIsFontFamilyOpen}
          value={currentFontFamily}
          onValueChange={(value) => {
            const { from, to } = selectDropdownSelectionRef.current || { from: 0, to: 0 };
            
            if (from !== to) {
              if (value === 'default') {
                editor.chain().focus().setTextSelection({ from, to }).unsetFontFamily().setTextSelection({ from, to }).run();
              } else {
                editor.chain().focus().setTextSelection({ from, to }).setFontFamily(value).setTextSelection({ from, to }).run();
              }
            } else {
              if (value === 'default') {
                editor.chain().focus().unsetFontFamily().run();
              } else {
                editor.chain().focus().setFontFamily(value).run();
              }
            }
            savedSelectionRef.current = { from, to, isNodeSelection: false, nodeType: null };
            selectDropdownSelectionRef.current = { from, to };
            setIsFontFamilyOpen(false);
          }}
        >
          <SelectTrigger
            className={`${isDesktop ? 'w-44' : 'w-28'} bg-white/80 dark:bg-slate-800/80 text-sm [&>span]:truncate`}
            onPointerDownCapture={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const { from, to } = editor.state.selection;
              selectDropdownSelectionRef.current = { from, to };
              setIsFontFamilyOpen(true);
            }}
          >
            <SelectValue>{currentFontFamily === 'default' ? t('editor.font') : currentFontFamily.split(',')[0].replace(/"/g, '')}</SelectValue>
          </SelectTrigger>
          <SelectContent
            className="editor-select-content"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <SelectItem value="default">{t('editor.fontNormal')}</SelectItem>
            {fontFamilyOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          open={isFontSizeOpen}
          onOpenChange={setIsFontSizeOpen}
          value={currentFontSize}
          onValueChange={(value) => {
            const { from, to } = selectDropdownSelectionRef.current || { from: 0, to: 0 };

            if (from !== to) {
              if (value === 'default') {
                editor.chain().focus().setTextSelection({ from, to }).unsetFontSize().setTextSelection({ from, to }).run();
              } else {
                editor.chain().focus().setTextSelection({ from, to }).setFontSize(value).setTextSelection({ from, to }).run();
              }
            } else {
              if (value === 'default') {
                editor.chain().focus().unsetFontSize().run();
              } else {
                editor.chain().focus().setFontSize(value).run();
              }
            }
            savedSelectionRef.current = { from, to, isNodeSelection: false, nodeType: null };
            selectDropdownSelectionRef.current = { from, to };
            setIsFontSizeOpen(false);
          }}
        >
          <SelectTrigger
            className={`${isDesktop ? 'w-32' : 'w-20'} bg-white/80 dark:bg-slate-800/80 text-sm [&>span]:truncate`}
            onPointerDownCapture={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const { from, to } = editor.state.selection;
              selectDropdownSelectionRef.current = { from, to };
              setIsFontSizeOpen(true);
            }}
          >
            <SelectValue>{currentFontSize === 'default' ? t('editor.size') : `${currentFontSize.replace('px', '')} px`}</SelectValue>
          </SelectTrigger>
          <SelectContent
            className="editor-select-content"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <SelectItem value="default">{t('editor.sizeNormal')}</SelectItem>
            {fontSizeOptions.map((size) => (
              <SelectItem key={size} value={size}>
                {size.replace('px', '')} px
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isDesktop ? (
        <>
          {/* Text Formatting Buttons.
              runWithSelection incluye .focus(): sin él, tras usar los dropdowns de
              fuente/tamaño (Radix devuelve el foco a su trigger) la marca se aplica
              pero el editor no recupera el foco y la selección deja de verse. */}
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleBold())}
            active={markActive('bold')}
            icon={Bold}
            label="Bold"
            title={t('editor.bold')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleItalic())}
            active={markActive('italic')}
            icon={Italic}
            label="Italic"
            title={t('editor.italic')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleStrike())}
            active={markActive('strike')}
            icon={Strikethrough}
            label="Strike"
            title={t('editor.strike')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleCode())}
            active={markActive('code')}
            icon={Code}
            label="Code"
            title={t('editor.code')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />

          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

          {/* Paragraph and Heading Buttons */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const { from, to } = editor.state.selection;
              if (from !== to) {
                editor.chain().focus().setTextSelection({ from, to }).setParagraph().setTextSelection({ from, to }).run();
              } else {
                editor.chain().focus().setParagraph().run();
              }
            }}
            className={`hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors ${
              editor.isActive('paragraph') ? 'bg-sky-200 text-sky-700 dark:bg-sky-800 dark:text-sky-200' : ''
            }`}
          >
            <span className="text-xs font-medium">{t('editor.normal')}</span>
          </Button>
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleHeading({ level: 1 }))}
            active={editor.isActive('heading', { level: 1 })}
            icon={Heading1}
            label="Heading 1"
            title={t('editor.heading1')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleHeading({ level: 2 }))}
            active={editor.isActive('heading', { level: 2 })}
            icon={Heading2}
            label="Heading 2"
            title={t('editor.heading2')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleHeading({ level: 3 }))}
            active={editor.isActive('heading', { level: 3 })}
            icon={Heading3}
            label="Heading 3"
            title={t('editor.heading3')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />

          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

          {/* List and Quote Buttons */}
          <ToolbarButton
            onClick={(selectionInfo) => {
              runWithSelection(selectionInfo, (chain) => chain.toggleBulletList());
            }}
            active={editor.isActive('bulletList')}
            icon={List}
            label="Bullet List"
            title={t('editor.bulletList')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => {
              runWithSelection(selectionInfo, (chain) => chain.toggleOrderedList());
            }}
            active={editor.isActive('orderedList')}
            icon={ListOrdered}
            label="Ordered List"
            title={t('editor.orderedList')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => {
              // En una lista, se envuelve/desenvuelve la LISTA COMPLETA en una cita
              // (conservando la lista dentro del blockquote). Fuera de lista, cita
              // normal por bloque. Se toma la lista más externa (shallowest) para que
              // una lista anidada se cite entera.
              const inList = editor.isActive('bulletList') || editor.isActive('orderedList');
              if (inList) {
                const { $from } = editor.state.selection;
                let listPos = null;
                for (let d = 1; d <= $from.depth; d++) {
                  const n = $from.node(d);
                  if (n.type.name === 'bulletList' || n.type.name === 'orderedList') {
                    listPos = $from.before(d);
                    break;
                  }
                }
                if (listPos != null) {
                  editor.chain().focus().setNodeSelection(listPos).toggleBlockquote().run();
                  return;
                }
              }
              runWithSelection(selectionInfo, (chain) => chain.toggleBlockquote());
            }}
            active={editor.isActive('blockquote')}
            icon={Quote}
            label="Quote"
            title={t('editor.quote')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />

          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

          {/* Alignment Buttons */}
          <ToolbarButton
            onClick={(selectionInfo) => {
              alignTableOrSelection('left', selectionInfo);
            }}
            active={alignActive('left')}
            icon={AlignLeft}
            label="Align Left"
            title={t('editor.alignLeft')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => {
              alignTableOrSelection('center', selectionInfo);
            }}
            active={alignActive('center')}
            icon={AlignCenter}
            label="Align Center"
            title={t('editor.alignCenter')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={(selectionInfo) => {
              alignTableOrSelection('right', selectionInfo);
            }}
            active={alignActive('right')}
            icon={AlignRight}
            label="Align Right"
            title={t('editor.alignRight')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />

          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

          {/* Highlight Button */}
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleHighlight())}
            active={markActive('highlight')}
            icon={Highlighter}
            label="Highlight"
            title={t('editor.highlight')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />

          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

          {/* Insert Buttons */}
          <ToolbarButton
            onClick={onImageUpload}
            icon={ImageIcon}
            label="Insert Image"
            title={t('editor.insertImage')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={onImageGenerate}
            icon={Sparkles}
            label="Generate Image"
            title={t('editor.generateImage')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={onOpenGallery}
            icon={Images}
            label="Gallery"
            title="Galería de imágenes"
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />

          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />

          <ToolbarButton
            onClick={onInsertTable}
            icon={TableIcon}
            label="Insert Smart Table"
            title={t('editor.insertTable')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
        </>
      ) : (
        <>
          {/* T-121b — Toolbar compacto (<1024px): Bold fijo + slot rotativo +
              menu de desborde. El slot reusa ToolbarButton con la acción actual. */}
          <ToolbarButton
            onClick={(selectionInfo) => runWithSelection(selectionInfo, (chain) => chain.toggleBold())}
            active={markActive('bold')}
            icon={Bold}
            label="Bold"
            title={t('editor.bold')}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          <ToolbarButton
            onClick={slotAction.run}
            active={slotAction.isActive ? slotAction.isActive() : false}
            icon={slotAction.icon}
            label={slotAction.label}
            title={slotAction.title}
            editor={editor}
            savedSelectionRef={savedSelectionRef}
          />
          {/* Menu de desborde — patrón de foco/selección copiado de los Select de
              fuente/tamaño: onPointerDownCapture captura el rango y abre manualmente
              (open controlado), modal={false} evita scroll-lock en móvil, y
              onCloseAutoFocus impide que Radix devuelva el foco al trigger tras
              onSelect (el bug documentado arriba para los dropdowns). */}
          <DropdownMenu open={isOverflowOpen} onOpenChange={setIsOverflowOpen} modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="toolbar-overflow-button"
                title="Mas herramientas"
                onPointerDownCapture={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const { from, to } = editor.state.selection;
                  selectDropdownSelectionRef.current = { from, to };
                  savedSelectionRef.current = { from, to, isNodeSelection: false, nodeType: null };
                  setIsOverflowOpen(true);
                }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="editor-select-content"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {overflowActions.map((a) => {
                const Icon = a.icon;
                return (
                  <DropdownMenuItem
                    key={a.id}
                    onSelect={() => {
                      // No leer editor.state.selection (perdió el foco): usar el
                      // rango capturado al abrir, igual que los Select de fuente/tamaño.
                      a.run(selectDropdownSelectionRef.current || savedSelectionRef.current);
                      setToolbarSlot(a.id);
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{a.title}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}

      {/* Chat IA toggle — resaltado cuando abierto, empujado a la derecha (ml-auto)
          cuando cerrado. ToolbarButton no acepta className propia para el fondo
          activo ni para ml-auto, así que usamos un <Button> plano del toolbar. */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onToggleChat}
        title="Chat IA"
        data-testid="chat-toggle-button"
        className={`transition-colors ${chatOpen ? 'bg-sky-500 text-white hover:bg-sky-600' : 'ml-auto hover:bg-sky-100 dark:hover:bg-sky-900/50'}`}
      >
        <MessageSquare className="w-4 h-4" />
        <span className="hidden sm:inline text-[10px]">Chat IA</span>
      </Button>
    </div>
  );
};

export default EditorToolbar;
